import { Injectable, inject } from '@angular/core';
import { SupabaseService } from '../../supabase.service';
import { AuthService } from '../../auth/auth.service';
import { Tables } from '../../../models/enums';

/** Familles d'événements suivies — cf. migration 20260923120000. */
export type VoiceAnalyticsEventType =
  | 'unsupported'
  | 'clarification'
  | 'executed'
  | 'failed'
  | 'flow_started'
  | 'flow_completed'
  | 'flow_abandoned';

export interface VoiceAnalyticsEvent {
  eventType: VoiceAnalyticsEventType;
  /** Endpoint de l'intent, ou identifiant du flow guidé. */
  endpoint?: string | null;
  /** Phrase brute — UNIQUEMENT pour `unsupported` / `clarification`, cf. `#shouldKeepTranscript`. */
  transcript?: string | null;
  inputMode?: 'voice' | 'text' | null;
  confidence?: number | null;
  /** Libellé court et agrégeable, jamais un message d'exception brut. */
  failureReason?: string | null;
}

/** Au-delà, on tronque : un transcript sert à comprendre une intention, pas à archiver un monologue. */
const MAX_TRANSCRIPT_CHARS = 300;
const MAX_FAILURE_REASON_CHARS = 120;

/**
 * Télémétrie de l'assistant conversationnel (MOKA-208, priorité 1).
 *
 * Objectif : savoir ce que les utilisateurs *tentent* de dire, pas seulement ce
 * qu'ils réussissent à faire. Sans cette mesure, élargir le catalogue d'actions
 * revient à deviner.
 *
 * Deux règles non négociables :
 * 1. **Jamais bloquant.** Un échec d'écriture ne doit pas dégrader la
 *    conversation : tout est fire-and-forget avec `console.warn`.
 * 2. **Transcript minimal.** La phrase brute n'est conservée que sur les
 *    échecs de compréhension, seuls cas où elle a une valeur d'analyse. Sur une
 *    action réussie, on sait déjà ce qui a été fait : la garder n'ajouterait
 *    que de la donnée personnelle à protéger.
 */
@Injectable({ providedIn: 'root' })
export class VoiceTelemetryService {
  private supabase = inject(SupabaseService);
  private auth = inject(AuthService);

  /**
   * Enregistre un événement. Ne retourne rien volontairement : aucun appelant
   * ne doit attendre la télémétrie ni adapter son comportement à son résultat.
   */
  track(event: VoiceAnalyticsEvent): void {
    const profile = this.auth.profile;
    if (!profile?.id || profile.organisation_id == null) return;

    const row = {
      profile_id: profile.id,
      organisation_id: profile.organisation_id,
      event_type: event.eventType,
      endpoint: event.endpoint ?? null,
      transcript: this.#transcriptFor(event),
      input_mode: event.inputMode ?? null,
      confidence: event.confidence ?? null,
      failure_reason: this.#truncate(event.failureReason, MAX_FAILURE_REASON_CHARS)
    };

    void this.supabase.client
      .from(Tables.VoiceAnalyticsEvents)
      .insert(row)
      .then(({ error }) => {
        if (error) console.warn('[VoiceTelemetry] événement non enregistré', error.message);
      });
  }

  /**
   * Le transcript n'est conservé que sur les échecs de compréhension : c'est là
   * qu'il renseigne (« que voulait dire l'utilisateur ? »). Ailleurs il ne
   * ferait qu'élargir inutilement la surface de données personnelles.
   */
  #transcriptFor(event: VoiceAnalyticsEvent): string | null {
    const keepsTranscript = event.eventType === 'unsupported' || event.eventType === 'clarification';
    if (!keepsTranscript) return null;
    return this.#truncate(event.transcript, MAX_TRANSCRIPT_CHARS);
  }

  #truncate(value: string | null | undefined, max: number): string | null {
    const trimmed = value?.trim();
    if (!trimmed) return null;
    return trimmed.length > max ? `${trimmed.slice(0, max)}…` : trimmed;
  }
}
